import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { Text, TextArea } from "@hopper-ui/components";
import { useId } from "react";
import { I18nProvider } from "react-aria-components";

export interface DraftEditor {
    value: string;
    onChange: (value: string) => void;
}

interface Props {
    editor: DraftEditor;
    label: string;
    // A limit the destination enforces; the field shows how much is left once the text nears it.
    maxLength?: number;
    isLabelVisible?: boolean;
    isInvalid?: boolean;
    isDisabled?: boolean;
}

const SHOW_REMAINING_BELOW = 100;

export function DraftTextArea({ editor, label, maxLength, isLabelVisible = false, isInvalid, isDisabled = false }: Props) {
    const { i18n } = useSharedTranslation();
    const labelId = useId();
    const isNearLimit = maxLength !== undefined && maxLength - editor.value.length < SHOW_REMAINING_BELOW;

    // The assistant roots give Hopper no locale, so the counter would read the browser's language instead of the panel's.
    const field = (
        <I18nProvider locale={i18n.language}>
            <TextArea
                aria-label={isLabelVisible ? undefined : label}
                aria-labelledby={isLabelVisible ? labelId : undefined}
                value={editor.value}
                onChange={editor.onChange}
                maxLength={maxLength}
                isInvalid={isInvalid}
                showCharacterCount={isNearLimit}
                isDisabled={isDisabled}
                minRows={3}
                maxRows={12}
                isFluid
                className="wai-pa-field"
            />
        </I18nProvider>
    );

    if (!isLabelVisible) {
        return field;
    }

    // Hopper's field label is a 16px heading; the card labels its field in 12px medium text, as the prototype does.
    return (
        <div className="wai-pa-field-group">
            <Text id={labelId} size="xs" fontWeight="body-xs-medium" color="neutral">{label}</Text>
            {field}
        </div>
    );
}
